<template>
  <div class="d-flex flex-column">
    <div
      v-if="['idle', 'progress'].includes(addObserverStatus)"
      class="pa-0 text-black mb-4"
    >
      {{ i18n.t('texts.add_observer_text_hint') }}
    </div>
    <v-form
      v-if="['idle', 'progress'].includes(addObserverStatus)"
      ref="addObserverForm"
      class="d-inline-flex ga-2"
      @submit.prevent="sendInvitation"
    >
      <EmailSuggestion
        :id="email"
        key="invitation-email-input"
        v-model.trim="email"
        autofocus
        data-test-id="forward-email"
        :label="i18n.t('labels.email')"
        :rules="[ruleEmail]"
      />
      <div>
        <v-switch
          v-model="isAdmin"
          class="v-size--small"
          hide-details
          inset
          @keydown.enter="isAdmin = !isAdmin"
        >
          <template #label>
            {{ i18n.t('labels.admin') }}
          </template>
        </v-switch>
      </div>
    </v-form>

    <v-alert
      v-if="addObserverStatus === 'error'"
      class="mb-5 text-body-small"
      data-test-id="deep-sign-alert"
      type="error"
      variant="tonal"
    >
      <div class="text-body-medium">
        {{
          i18n.t(isDuplicate ? 'errors.observer_duplicate' : 'errors.generic')
        }}
      </div>
    </v-alert>

    <portal to="document-dialog-actions">
      <VBtnSecondary variant="outlined" @click="closeDialog">{{
        i18n.t(
          addObserverStatus === 'error' ? 'buttons.close' : 'buttons.cancel'
        )
      }}</VBtnSecondary>
      <VBtnPrimary
        v-if="addObserverStatus !== 'error'"
        color="primary"
        data-test-id="send-invitation"
        :disabled="!canSend"
        :loading="addObserverStatus === 'progress'"
        @click="sendInvitation"
        >{{ i18n.t('buttons.send_invitation') }}
      </VBtnPrimary>

      <VBtnPrimary
        v-if="addObserverStatus === 'error' && isDuplicate"
        color="primary"
        @click="retry"
        >{{ i18n.t('buttons.retry') }}
      </VBtnPrimary>
    </portal>
  </div>
</template>

<script setup lang="ts">
import EmailSuggestion from '@/components/inputs/Emailsuggestion/EmailSuggestion.vue';
import { useEmailService } from '@/components/inputs/Emailsuggestion/useEmailService';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useMessageStore } from '@/stores/message.ts';
import type { ComponentState } from '@/types/ui';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AuthService from '@/services/authService.ts';
import { EMAIL_REGEX, ruleEmail } from '@deepcloud/field-validators';
import { useApi } from '@/composables/useApi.ts';

const i18n = useI18n();
const { deepSignAPI } = useApi();
const messageStore = useMessageStore();
const addObserverForm = ref();
const email = ref<string | null>(null);
const isAdmin = ref(false);
const emit = defineEmits(['closeDialog']);
const addObserverStatus = ref<ComponentState>('idle');
const { currentDocument } = useDocumentContext();
const isDuplicate = ref(false);
const { saveRecentlyUsedEmails } = useEmailService();

function closeDialog() {
  email.value = null;
  emit('closeDialog');
}

const canSend = computed(() => {
  return !!email.value && EMAIL_REGEX.test(email.value);
});

async function sendInvitation() {
  try {
    addObserverStatus.value = 'progress';
    const isValid = addObserverForm.value.validate();
    if (email.value && isValid && currentDocument.value) {
      // Update preference with new emails
      if (AuthService.isAuthenticated) {
        try {
          await saveRecentlyUsedEmails([{ email: email.value }]);
        } catch (error) {
          console.error(error);
        }
      }

      const response = await deepSignAPI.value.documentsObservers.create(
        currentDocument.value.documentId,
        {
          email: email.value,
          isAdmin: isAdmin.value
        }
      );

      currentDocument.value.observers.push(response.data);
      messageStore.showMessage({
        key: 'snackbars.observer_added',
        color: 'success',
        icon: 'fa fa-circle-check'
      });
      closeDialog();
      addObserverStatus.value = 'success';
    } else {
      addObserverStatus.value = 'idle';
    }
  } catch (error) {
    if (error.response.status === 409) {
      isDuplicate.value = true;
    }
    addObserverStatus.value = 'error';
  }
}

async function retry() {
  email.value = null;
  addObserverStatus.value = 'idle';
  isDuplicate.value = false;
}
</script>

<style scoped lang="scss">
:deep(.v-text-field.v-text-field--enclosed) .v-text-field__details {
  padding-left: 0 !important;
}

.v-input :deep(.v-input__details) {
  padding-left: 0%;
  padding-top: 8px;
  min-height: 18px;
}

.v-input.v-text-field {
  margin-bottom: 0.75rem;
}

.observer-email-input :deep(.slide-y-transition-leave-active) {
  transition: none !important;
}

.observer-email-input :deep(.slide-y-transition-leave-to) {
  transition: none !important;
}

.observer-email-input :deep(.slide-y-transition-enter-active) {
  transition: none !important;
}
.observer-email-input :deep(.slide-y-transition-enter-to) {
  transition: none !important;
}
</style>
